import React, { ReactElement } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import IconSelector from "components/shared/images/IconSelector";

import { getSelectedCloudboxData } from "redux/selectors/cloudboxSelector";

import CreateSubaccount from "../actions/CreateSubaccount";

import SubaccountActions from "./SubaccountActions";

const CloudboxSubaccounts = (): ReactElement => {
 const cloudboxData = useSelector(getSelectedCloudboxData);

 return (
  <Paper elevation={1} sx={{ borderRadius: "5px", p: 2 }}>
   <Stack direction="row" justifyContent="space-between">
    <Stack alignItems="center" justifyContent="flex-start" direction="row">
     <IconSelector icon="AccountBoxIcon" props={{ fontSize: "medium", color: "kxOrange" }} />
     <Typography variant="unboundedTitle" ml={1} fontSize={16}>
      <FormattedMessage id="addons.cloudbox.subaccounts" />
     </Typography>
    </Stack>
    {cloudboxData && <CreateSubaccount />}
   </Stack>
   {cloudboxData?.subaccounts && cloudboxData.subaccounts.length > 0 ? (
    <Paper elevation={1} sx={{ mt: 2, borderRadius: "5px" }}>
     <TableContainer component="div">
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
       <TableHead>
        <TableRow sx={{ backgroundColor: "transparent" }}>
         <TableCell>
          <Typography variant="kxColoredSmall" fontWeight="bold">
           <FormattedMessage id="addons.cloudbox.username" />
          </Typography>
         </TableCell>
         <TableCell>
          <Typography variant="kxColoredSmall" fontWeight="bold">
           <FormattedMessage id="addons.cloudbox.directory" />
          </Typography>
         </TableCell>
         <TableCell align="center">
          <Typography variant="kxColoredSmall" fontWeight="bold">
           <FormattedMessage id="addons.cloudbox.readonly" />
          </Typography>
         </TableCell>
         <TableCell>
          <Typography variant="kxColoredSmall" fontWeight="bold">
           <FormattedMessage id="addons.cloudbox.settings" />
          </Typography>
         </TableCell>
         <TableCell>
          <Typography variant="kxColoredSmall" fontWeight="bold">
           <FormattedMessage id="addons.cloudbox.actions" />
          </Typography>
         </TableCell>
        </TableRow>
       </TableHead>
       <TableBody>
        {cloudboxData.subaccounts.map((row, index) => {
         return (
          <TableRow key={`subaccounts-list-${index}`}>
           <TableCell component="th" scope="row">
            <Stack direction="column">
             <Typography variant="inherit" fontWeight="bold">
              {row.username}
             </Typography>
             <Typography variant="extraSmall" fontStyle="italic">
              {row.description || "No deacription"}
             </Typography>
            </Stack>
           </TableCell>
           <TableCell component="th" scope="row">
            <Stack direction="row" alignContent="center" spacing={0.5}>
             {`/${row.home_directory}`}
            </Stack>
           </TableCell>
           <TableCell component="th" scope="row">
            <Stack direction="row" alignItems="center" justifyContent="center">
             <IconSelector
              icon={row.access_settings.readonly ? "CheckCircleIcon" : "CancelIcon"}
              props={{ color: row.access_settings.readonly ? "success" : "error" }}
             />
            </Stack>
           </TableCell>
           <TableCell>
            <Stack spacing={1}>
             <Stack direction="row" spacing={2}>
              <Stack direction="row">
               <Typography variant="caption" minWidth={60}>
                Samba
               </Typography>
               <IconSelector
                icon={row.access_settings.samba_enabled ? "CheckCircleIcon" : "CancelIcon"}
                props={{ color: row.access_settings.samba_enabled ? "success" : "error" }}
               />
              </Stack>
              <Stack direction="row">
               <Typography variant="caption" minWidth={60}>
                Webdav
               </Typography>
               <IconSelector
                icon={row.access_settings.webdav_enabled ? "CheckCircleIcon" : "CancelIcon"}
                props={{ color: row.access_settings.webdav_enabled ? "success" : "error" }}
               />
              </Stack>
             </Stack>
             <Stack direction="row" spacing={2}>
              <Stack direction="row">
               <Typography variant="caption" minWidth={60}>
                SSH
               </Typography>
               <IconSelector
                icon={row.access_settings.ssh_enabled ? "CheckCircleIcon" : "CancelIcon"}
                props={{ color: row.access_settings.ssh_enabled ? "success" : "error" }}
               />
              </Stack>
              <Stack direction="row">
               <Typography variant="caption" minWidth={60}>
                R. Extern
               </Typography>
               <IconSelector
                icon={row.access_settings.reachable_externally ? "CheckCircleIcon" : "CancelIcon"}
                props={{
                 color: row.access_settings.reachable_externally ? "success" : "error"
                }}
               />
              </Stack>
             </Stack>
            </Stack>
           </TableCell>
           <TableCell>
            <SubaccountActions rowData={row} />
           </TableCell>
          </TableRow>
         );
        })}
       </TableBody>
      </Table>
     </TableContainer>
    </Paper>
   ) : (
    <Stack sx={{ p: 2 }}>
     <Alert severity="info">
      <Typography variant="subtitle2">
       <FormattedMessage id="addons.cloudbox.noSubaccountsFound" />
      </Typography>
     </Alert>
    </Stack>
   )}
  </Paper>
 );
};

export default CloudboxSubaccounts;
